import React, { isValidElement } from 'react';
import { observer } from 'mobx-react-lite';
import { NoSidebarPageLayout } from '@insurancemenu/imenu-components-lib';
import { NoSidebarPageLayoutProps } from '@insurancemenu/imenu-components-lib/dist/types';
import { bpHistory } from 'global/bpHistory';
import { useNavigation } from 'global/hooks/useNavigation';
import { ROUTES_CONFIG_V2, RouteV2Id } from 'routes/routesConfigV2';
import { LayoutPageFooterProps, LayoutPageHeaderProps } from '../types';

export interface PageInFlowNoSidebarLayoutProps
  extends Omit<NoSidebarPageLayoutProps, 'breadcrumbs' | 'pageHeader' | 'pageFooter'> {
  domainParams: Record<string, string | number | undefined>;
  pageId: RouteV2Id;
  pageFooter?: LayoutPageFooterProps | null;
  pageHeader?: LayoutPageHeaderProps;
}

/** Integrates the no sidebar page with the useNavigation hook to handle routing and navigation state. */
const PageInFlowNoSidebarLayout: React.FC<PageInFlowNoSidebarLayoutProps> = observer(
  ({ domainParams, pageId, pageFooter, pageHeader, ...rest }) => {
    const { breadcrumbs, onBack } = useNavigation({ domainParams });
    const pageConfig = ROUTES_CONFIG_V2[pageId];

    function buildPageFooter(): NoSidebarPageLayoutProps['pageFooter'] {
      if (pageFooter === null) {
        return undefined;
      }
      if (isValidElement(pageFooter)) {
        return pageFooter;
      }
      if (pageFooter !== null && typeof pageFooter === 'object') {
        const footerProps = pageFooter as { onBack?: () => void };
        return { ...footerProps, onBack: footerProps.onBack ?? onBack };
      }
      return { onBack };
    }

    return (
      <NoSidebarPageLayout
        breadcrumbs={{
          crumbs: breadcrumbs.map((crumb) => ({
            label: crumb.label,
            onClick: (): void => {
              bpHistory.push(crumb.url);
            },
          })),
        }}
        pageHeader={{
          title: pageConfig.title,
          subtitle: pageConfig.subtitle,
          onBackButtonClick: onBack,
          ...pageHeader,
        }}
        pageFooter={buildPageFooter()}
        {...rest}
      />
    );
  },
);

export default PageInFlowNoSidebarLayout;
